import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Assessment } from '../types';
import { assessmentService } from '../services/assessmentService';
import { questionService } from '../services/questionService';
import { maturityScoringService, MaturityAnalysis } from '../services/maturityScoringService';
import MaturitySpiderChart from '../components/MaturitySpiderChart';
import PDFReportGenerator from '../components/PDFReportGenerator';
import TagBasedAssessmentComponent from '../components/TagBasedAssessment';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { tagBasedScoringService, TagBasedAssessment } from '../services/tagBasedScoringService';
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  LightBulbIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import MaturityDefinitions from '../components/MaturityDefinitions';
import { useHeader } from '../contexts/HeaderContext';

// Helper to get progression label
const getProgressionLabel = (percentage: number): string => {
  if (percentage <= 20) return 'Initial';
  if (percentage <= 40) return 'Managed';
  if (percentage <= 60) return '(Re)Defined';
  if (percentage <= 80) return 'Measured';
  return 'Optimizing';
};

// Helper to get progression color
const getProgressionColor = (percentage: number): string => {
  if (percentage <= 20) return '#6366F1'; // Indigo-500
  if (percentage <= 40) return '#4F46E5'; // Indigo-600
  if (percentage <= 60) return '#4338CA'; // Indigo-700
  if (percentage <= 80) return '#3730A3'; // Indigo-800
  return '#312E81'; // Indigo-900
};

const MaturityDashboard: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const assessmentId = parseInt(id || '0');
  const { user } = useAuth();
  const { setTitle } = useHeader();
  // Check role in a case-insensitive way to handle both uppercase (backend) and lowercase (frontend) formats
  const isAdvisor = ['ADVISOR', 'ADMIN', 'SUPER_ADMIN', 'advisor', 'admin', 'super_admin'].includes(user?.role || '');

  // Check for 'assessments:view_scores' permission
  const canViewScores = user?.permissions?.includes('assessments:view_scores') ||
    user?.permissions?.includes('admin:full') ||
    isAdvisor; // Fallback for backward compatibility

  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [maturityAnalysis, setMaturityAnalysis] = useState<MaturityAnalysis | null>(null);
  const [questions, setQuestions] = useState<any[]>([]);
  const [tagAssessments, setTagAssessments] = useState<TagBasedAssessment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        // Load assessment data
        const assessmentData = await assessmentService.getAssessment(assessmentId);
        const [questionsData, responsesData] = await Promise.all([
          questionService.getQuestions({
            questionnaireLabel: assessmentData.questionnaireLabel || undefined,
            questionnaireVersion: assessmentData.questionnaireVersion || undefined
          }),
          assessmentService.getAssessmentResponses(assessmentId)
        ]);

        setAssessment(assessmentData);

        if (assessmentData?.client) {
          setTitle(`${assessmentData.client.name} Evaluation Analysis`);
        }

        setQuestions(questionsData);

        // Calculate maturity scores
        console.log('Calculating maturity scores with:', {
          assessmentId: assessmentData.id,
          questionsCount: questionsData.length,
          responsesCount: responsesData.length,
          sampleResponses: responsesData.slice(0, 3).map(r => ({
            questionId: r.questionId,
            answerValue: r.answerValue,
            hasValue: !!r.answerValue
          }))
        });

        const analysis = maturityScoringService.calculateMaturityScores(
          assessmentData,
          questionsData,
          responsesData
        );

        console.log('Maturity analysis result:', {
          dimensionScores: analysis.dimensionScores.map(d => ({
            dimension: d.dimension,
            score: d.score,
            maxScore: d.maxScore,
            percentage: d.percentage,
            answeredCount: d.answeredCount,
            questionCount: d.questionCount
          }))
        });

        setMaturityAnalysis(analysis);

        // Calculate tag-based assessments (e.g., AI Readiness)
        const tagAssessments = tagBasedScoringService.calculateTagBasedAssessments(
          questionsData,
          responsesData,
          ['AI'] // You can add more tags here
        );
        setTagAssessments(tagAssessments);

      } catch (error: any) {
        console.error('Failed to load maturity dashboard:', error);
        toast.error('Failed to load maturity analysis');
      } finally {
        setIsLoading(false);
      }
    };

    if (assessmentId) {
      loadData();
    }
  }, [assessmentId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!assessment || !maturityAnalysis) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Evaluation not found</h3>
        <button
          onClick={() => navigate(`/assessments/${assessmentId}`)}
          className="mt-4 btn btn-primary"
        >
          Back to Evaluations
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start space-x-3 sm:space-x-4 min-w-0">
          <button
            onClick={() => navigate(`/assessments/${assessmentId}`)}
            className="text-gray-400 hover:text-gray-600 mt-1"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 break-words">
              Maturity Analysis
            </h1>
            <p className="mt-1 text-sm sm:text-lg text-gray-500 break-words">
              {assessment.client?.name} • Evaluation #{assessment.id}
            </p>
          </div>
        </div>

        <div className="w-full lg:w-auto">
          <PDFReportGenerator
            assessment={assessment}
            maturityAnalysis={maturityAnalysis}
            clientName={assessment.client?.name || 'Unknown Client'}
            advisorName={`${assessment.advisor?.firstName || ''} ${assessment.advisor?.lastName || ''}`.trim() || 'Unknown Advisor'}
            canViewScores={canViewScores}
            className="inline-flex w-full sm:w-auto items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>
      </div>


      {/* Maturity Definitions Infographic */}
      <MaturityDefinitions />

      {/* Industry Standard Maturity - Primary Display */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <div className="mb-4">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Industry Standard Maturity</h2>
          <p className="text-sm text-gray-500 mt-1">
            Evaluation scores compared to industry best practices and standards
          </p>
        </div>
        <MaturitySpiderChart
          data={maturityAnalysis.dimensionScores}
          title="Maturity Evaluation by Dimension"
          size="large"
          showPoints={canViewScores}
        />
      </div>



      {/* Industry Standard Analysis - Strengths & Areas for Improvement */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <details className="group" open>
          <summary className="cursor-pointer list-none">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Strengths & Areas for Improvement</h3>
                <p className="text-sm text-gray-500 mt-1">Analysis of your top performing areas and opportunities for growth</p>
              </div>
              <svg
                className="w-5 h-5 text-gray-500 transition-transform group-open:rotate-180"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </summary>
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Strengths */}
              <div className="bg-green-50/50 rounded-lg border border-green-100 p-4 sm:p-6">
                <div className="flex items-center mb-4">
                  <LightBulbIcon className="h-6 w-6 text-green-500 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">Strengths</h3>
                </div>

                {canViewScores ? (
                  // Original List View for Admins/Advisors
                  <div className="space-y-3">
                    {maturityAnalysis.strengths.map((strength) => {
                      const dimension = maturityAnalysis.dimensionScores.find(d => d.dimension === strength);
                      return (
                        <div key={strength} className="flex items-start justify-between gap-3 p-3 bg-white rounded-lg border border-green-100 shadow-sm">
                          <span className="font-medium text-gray-900 break-words flex-1 min-w-0">{strength}</span>
                          <span className="text-green-600 font-bold shrink-0">
                            {dimension?.percentage.toFixed(0)}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  // Progression Bar View for Clients
                  <div className="space-y-4">
                    {maturityAnalysis.strengths.map((strength) => {
                      const dimension = maturityAnalysis.dimensionScores.find(d => d.dimension === strength);
                      return (
                        <div key={strength} className="p-3 bg-white rounded-lg border border-green-100 shadow-sm">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium text-gray-900 break-words min-w-0">{strength}</span>
                            <span className="text-xs font-semibold text-gray-500 uppercase">
                              {getProgressionLabel(dimension?.percentage || 0)}
                            </span>
                          </div>
                          <div className="relative w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="absolute top-0 left-0 h-full rounded-full transition-all duration-300"
                              style={{
                                width: `${dimension?.percentage || 0}%`,
                                backgroundColor: getProgressionColor(dimension?.percentage || 0)
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Areas for Improvement */}
              <div className="bg-orange-50/50 rounded-lg border border-orange-100 p-4 sm:p-6">
                <div className="flex items-center mb-4">
                  <ExclamationTriangleIcon className="h-6 w-6 text-orange-500 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">Areas for Improvement</h3>
                </div>

                {canViewScores ? (
                  // Original List View for Admins/Advisors
                  <div className="space-y-3">
                    {maturityAnalysis.weaknesses.map((weakness) => {
                      const dimension = maturityAnalysis.dimensionScores.find(d => d.dimension === weakness);
                      return (
                        <div key={weakness} className="flex items-start justify-between gap-3 p-3 bg-white rounded-lg border border-orange-100 shadow-sm">
                          <span className="font-medium text-gray-900 break-words flex-1 min-w-0">{weakness}</span>
                          <span className="text-orange-600 font-bold shrink-0">
                            {dimension?.percentage.toFixed(0)}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  // Progression Bar View for Clients
                  <div className="space-y-4">
                    {maturityAnalysis.weaknesses.map((weakness) => {
                      const dimension = maturityAnalysis.dimensionScores.find(d => d.dimension === weakness);
                      return (
                        <div key={weakness} className="p-3 bg-white rounded-lg border border-orange-100 shadow-sm">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium text-gray-900 break-words min-w-0">{weakness}</span>
                            <span className="text-xs font-semibold text-gray-500 uppercase">
                              {getProgressionLabel(dimension?.percentage || 0)}
                            </span>
                          </div>
                          <div className="relative w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="absolute top-0 left-0 h-full rounded-full transition-all duration-300"
                              style={{
                                width: `${dimension?.percentage || 0}%`,
                                backgroundColor: getProgressionColor(dimension?.percentage || 0)
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </details>
      </div>

      {/* Industry Standard Recommendations */}
      <div className="hidden bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <details className="group">
          <summary className="cursor-pointer list-none">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <DocumentTextIcon className="h-6 w-6 text-blue-500 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Strategic Recommendations</h3>
              </div>
              <svg
                className="w-5 h-5 text-gray-500 transition-transform group-open:rotate-180"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </summary>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="space-y-3">
              {maturityAnalysis.recommendations.map((recommendation, index) => (
                <div key={index} className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </div>
                  <p className="text-gray-700">{recommendation}</p>
                </div>
              ))}
            </div>
          </div>
        </details>
      </div>

      {/* Detailed Dimension Breakdown */}
      <div className="hidden bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <details className="group" open>
          <summary className="cursor-pointer list-none">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Detailed Dimension Analysis</h3>
                <p className="text-sm text-gray-500 mt-1">Breakdown of scores and completion status for each evaluation dimension</p>
              </div>
              <svg
                className="w-5 h-5 text-gray-500 transition-transform group-open:rotate-180"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </summary>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="space-y-4">
              {[...maturityAnalysis.dimensionScores]
                .sort((a, b) => {
                  // Sort by displayOrder if available, otherwise maintain order
                  const aOrder = questions.find(q =>
                    (q.dimension?.label === a.dimension || q.dimension?.name === a.dimension)
                  )?.dimension?.displayOrder ?? 999;
                  const bOrder = questions.find(q =>
                    (q.dimension?.label === b.dimension || q.dimension?.name === b.dimension)
                  )?.dimension?.displayOrder ?? 999;
                  return aOrder - bOrder;
                })
                .map((dimension) => (
                  <div key={dimension.dimension} className="border border-gray-200 rounded-lg p-3 sm:p-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between mb-3">
                      <h4 className="font-medium text-gray-900 break-words">{dimension.dimension}</h4>
                      <div className="sm:text-right">
                        {canViewScores ? (
                          <>
                            <div className="text-lg font-bold text-gray-900">
                              {dimension.percentage.toFixed(1)}%
                            </div>
                            <div className="text-sm text-gray-500">
                              {dimension.score.toFixed(0)} / {dimension.maxScore.toFixed(0)} points
                            </div>
                          </>
                        ) : (
                          // For clients without view_scores permission, show completion status only
                          <div className="text-sm font-semibold text-gray-600">
                            {getProgressionLabel(dimension.percentage)}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="relative w-full h-4 bg-gray-100 rounded-full overflow-hidden">
                      {/* Faded bar for completion (Visual indication of effort) */}
                      {!canViewScores && (
                        <div
                          className="absolute top-0 left-0 h-full bg-gray-300 transition-all duration-300"
                          style={{
                            width: `${(dimension.answeredCount / dimension.questionCount) * 100}%`,
                          }}
                          title="Questions Answered"
                        ></div>
                      )}

                      {/* Score Bar (Visual indication of maturity) */}
                      <div
                        className={`absolute top-0 left-0 h-full rounded-full transition-all duration-300 ${!canViewScores ? 'opacity-80' : ''}`}
                        style={{
                          width: `${dimension.percentage}%`,
                          backgroundColor: getProgressionColor(dimension.percentage),
                          zIndex: 10
                        }}
                        title={canViewScores ? `Score: ${dimension.percentage.toFixed(0)}%` : getProgressionLabel(dimension.percentage)}
                      ></div>
                    </div>

                    <div className="flex flex-wrap justify-between gap-2 text-sm text-gray-500 mt-2">
                      <span>{dimension.answeredCount} of {dimension.questionCount} questions answered</span>
                      <span>
                        {dimension.answeredCount > 0 ?
                          Math.round((dimension.answeredCount / dimension.questionCount) * 100) : 0
                        }% completion
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </details>
      </div>

      {/* Tag-Based Secondary Assessments (e.g., AI Readiness) - Moved to bottom */}
      {tagAssessments.map((tagAssessment) => (
        <div key={tagAssessment.tag} className="bg-gray-50 rounded-lg border border-gray-200 p-4 sm:p-6">
          <details className="group">
            <summary className="cursor-pointer list-none">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-700">{tagAssessment.displayName}</h3>
                  <p className="text-sm text-gray-500 mt-1">{tagAssessment.description}</p>
                </div>
                <svg
                  className="w-5 h-5 text-gray-500 transition-transform group-open:rotate-180"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </summary>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <TagBasedAssessmentComponent
                assessment={tagAssessment}
                showPoints={canViewScores}
              />
            </div>
          </details>
        </div>
      ))}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row sm:justify-end gap-3 pt-6 border-t border-gray-200">
        <button
          onClick={() => navigate(`/assessments/${assessmentId}/questionnaire`)}
          className="w-full sm:w-auto px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Continue Evaluation
        </button>
        <button
          onClick={() => navigate(`/assessments/${assessmentId}`)}
          className="w-full sm:w-auto px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
        >
          Back to Evaluations
        </button>
      </div>
    </div>
  );
};

export default MaturityDashboard;
